'use client';

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download } from 'lucide-react';

type Unit = 'usd_month' | 'usd_quarter' | 'usd_year' | 'users_month';

type Item = {
  id: string;
  name: string;
  valuePerPeriod: number;
  unit: Unit;
  probability: number;
  monthsDelayed: number;
  jobSize: number;
};

const UNIT_OPTIONS: { value: Unit; label: string }[] = [
  { value: 'usd_month', label: '$/month' },
  { value: 'usd_quarter', label: '$/quarter' },
  { value: 'usd_year', label: '$/year' },
  { value: 'users_month', label: 'users/month' },
];

const STORAGE_KEY = 'planetroadmap.cost-of-delay-calculator.v1';

function newItem(name = ''): Item {
  return {
    id: Math.random().toString(36).slice(2),
    name,
    valuePerPeriod: 10000,
    unit: 'usd_month',
    probability: 80,
    monthsDelayed: 3,
    jobSize: 2,
  };
}

function monthlyValue(i: Item): number {
  const adjusted = i.valuePerPeriod * (i.probability / 100);
  if (i.unit === 'usd_quarter') return adjusted / 3;
  if (i.unit === 'usd_year') return adjusted / 12;
  return adjusted;
}

function totalCostOfDelay(i: Item): number {
  return monthlyValue(i) * i.monthsDelayed;
}

function cd3(i: Item): number {
  if (!i.jobSize) return 0;
  return monthlyValue(i) / i.jobSize;
}

function formatCurrency(n: number, unit: Unit): string {
  if (unit === 'users_month') return `${Math.round(n).toLocaleString()} user-months`;
  return `$${Math.round(n).toLocaleString()}`;
}

export function CostOfDelayCalculator() {
  const [items, setItems] = useState<Item[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Item[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setItems(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setItems([
      { ...newItem('Fix slow checkout'), valuePerPeriod: 50000, unit: 'usd_month', probability: 90, monthsDelayed: 3, jobSize: 1 },
      { ...newItem('New analytics dashboard'), valuePerPeriod: 20000, unit: 'usd_quarter', probability: 70, monthsDelayed: 6, jobSize: 3 },
      { ...newItem('Mobile app launch'), valuePerPeriod: 200000, unit: 'usd_year', probability: 60, monthsDelayed: 4, jobSize: 6 },
    ]);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [items, hydrated]);

  const sorted = useMemo(
    () =>
      [...items]
        .map((i) => ({ ...i, codTotal: totalCostOfDelay(i), cd3Score: cd3(i) }))
        .sort((a, b) => b.codTotal - a.codTotal),
    [items],
  );

  function update(id: string, patch: Partial<Item>) {
    setItems((curr) => curr.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }

  function remove(id: string) {
    setItems((curr) => curr.filter((i) => i.id !== id));
  }

  function add() {
    setItems((curr) => [...curr, newItem()]);
  }

  function reset() {
    if (!confirm('Clear all items and start over?')) return;
    setItems([newItem()]);
  }

  function exportCsv() {
    const rows = [
      [
        'Name',
        'Value per period',
        'Unit',
        'Probability (%)',
        'Months delayed',
        'Cost of Delay',
        'Job size (months)',
        'CD3',
      ],
      ...sorted.map((i) => [
        i.name || 'Untitled',
        i.valuePerPeriod,
        UNIT_OPTIONS.find((u) => u.value === i.unit)?.label ?? i.unit,
        i.probability,
        i.monthsDelayed,
        formatCurrency(i.codTotal, i.unit),
        i.jobSize,
        i.jobSize > 0 ? formatCurrency(i.cd3Score, i.unit) : '—',
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cost-of-delay.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading calculator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground w-8">#</th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground min-w-[180px]">
                  Feature
                </th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Value</th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">Unit</th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">
                  Probability
                  <div className="text-[10px] font-normal text-muted-foreground/70">%</div>
                </th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">
                  Delay
                  <div className="text-[10px] font-normal text-muted-foreground/70">months</div>
                </th>
                <th className="text-right py-3 px-4 font-semibold text-primary">Cost of Delay</th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">
                  Job size
                  <div className="text-[10px] font-normal text-muted-foreground/70">months</div>
                </th>
                <th className="text-right py-3 px-4 font-semibold text-primary">CD3</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((item, i) => (
                <tr key={item.id} className={i < sorted.length - 1 ? 'border-b border-border/40' : ''}>
                  <td className="py-2 px-4 text-muted-foreground font-mono text-xs">{i + 1}</td>
                  <td className="py-2 px-4">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => update(item.id, { name: e.target.value })}
                      placeholder="Untitled feature"
                      className="w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="number"
                      min={0}
                      value={item.valuePerPeriod}
                      onChange={(e) =>
                        update(item.id, { valuePerPeriod: Math.max(0, Number(e.target.value)) })
                      }
                      className="w-28 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <select
                      value={item.unit}
                      onChange={(e) => update(item.id, { unit: e.target.value as Unit })}
                      className="bg-transparent border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={item.probability}
                      onChange={(e) =>
                        update(item.id, {
                          probability: Math.max(0, Math.min(100, Number(e.target.value))),
                        })
                      }
                      className="w-20 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={item.monthsDelayed}
                      onChange={(e) =>
                        update(item.id, { monthsDelayed: Math.max(0, Number(e.target.value)) })
                      }
                      className="w-20 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-4 text-right font-mono font-semibold text-primary">
                    {formatCurrency(item.codTotal, item.unit)}
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={item.jobSize}
                      onChange={(e) =>
                        update(item.id, { jobSize: Math.max(0, Number(e.target.value)) })
                      }
                      className="w-20 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-4 text-right font-mono font-semibold text-primary">
                    {item.jobSize > 0 ? formatCurrency(item.cd3Score, item.unit) : '—'}
                  </td>
                  <td className="py-2 px-2">
                    <button
                      onClick={() => remove(item.id)}
                      aria-label="Remove item"
                      className="text-muted-foreground hover:text-red-500 p-1 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-muted-foreground">
                    No features yet. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          onClick={add}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
        >
          <Plus className="w-4 h-4" />
          Add feature
        </button>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={sorted.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Your scores are saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}
